/**
 * Hook to handle sending messages and notes from the chat input.
 * Uploads attachments before sending. On upload failure, blocks send and restores input.
 */

import {
  useConfirmFileUploadMutation,
  useRequestUploadUrlMutation,
} from '@saaga/shared/store/file-agents-endpoints.store'
import { logger } from '@saaga/shared/utils/logger.util'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'

import type { ChatAttachment } from '@/components/chat/chat-input-attachment.interface'
import { uploadMultipleFiles } from '@/services/file-upload.service'

import type { UseChatInputReturn } from './use-chat-input.hook'
import type { UseChatMessagesReturn } from './use-chat-messages.hook'

interface UseChatSendHandlerParams {
  input: UseChatInputReturn
  messages: UseChatMessagesReturn
}

export interface UseChatSendHandlerReturn {
  handleSend: () => void
  isUploading: boolean
  uploadProgress: number
}

const getFilesFromAttachments = (attachmentList: ChatAttachment[]): File[] =>
  attachmentList.flatMap((a) => (a.file ? [a.file] : []))

export const useChatSendHandler = ({
  input,
  messages,
}: UseChatSendHandlerParams): UseChatSendHandlerReturn => {
  const [requestUploadUrl] = useRequestUploadUrlMutation()
  const [confirmUpload] = useConfirmFileUploadMutation()
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)

  const handleSend = useCallback(async () => {
    if (!input.inputValue.trim() && input.attachmentList.length === 0) return
    const content = input.inputValue
    const currentAttachmentList = [...input.attachmentList]
    input.clearInput()

    if (input.inputMode === 'note') {
      messages.sendNote(content)
      return
    }

    const fileList = getFilesFromAttachments(currentAttachmentList)
    if (fileList.length === 0) {
      messages.sendMessage(content)
      return
    }

    setIsUploading(true)
    setUploadProgress(0)

    const progressMap = new Map<string, number>()

    try {
      const resultList = await uploadMultipleFiles(fileList, {
        requestUploadUrl,
        confirmUpload,
        onProgress: (fileId: string, progress: number) => {
          progressMap.set(fileId, progress)
          const total = Array.from(progressMap.values()).reduce((sum, p) => sum + p, 0)
          setUploadProgress(Math.round(total / fileList.length))
        },
      })
      const attachmentIdList = resultList.map((r) => r.fileId)
      messages.sendMessage(content, attachmentIdList)
    } catch (error) {
      logger.error('Failed to upload attachments', error)
      toast.error('Failed to upload attachments. Please try again.')
      input.setInputValue(content)
      input.restoreAttachmentList(currentAttachmentList)
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
    }
  }, [input, messages, requestUploadUrl, confirmUpload])

  return { handleSend, isUploading, uploadProgress }
}
